import React, { useState, useMemo } from 'react';
import { Box, Typography, RadioGroup, FormControlLabel, Radio, Card, CircularProgress, Backdrop } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import ByzMuiButton from 'src/components/core-components/mui/buttons/ByzMuiButton';
import ByzMuiDataGrid from 'src/components/core-components/mui/data-grid';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';
import { useTranslate } from 'src/locales';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import { nurseService } from 'src/api/nurse/nurseService';
import ByzMuiSnackBar from 'src/components/core-components/mui/ByzMuiSnackBar';
import dayjs from 'dayjs';
import CancelDialog from 'src/components/core-components/mui/CancalDialog';




interface ReturnedPatientOrder {
  plandate: string;
  srdid: number;
  srumid?: number;
  fqcanddose: string;
  matname: string;
  srummatnames: string;
  status: number;
  statusname: string;
  reqstatusname: string;
  ismixinginfo: string;
  appdosedet: string;
}


interface ReturnMenuProps {
  patientId?: string | number;
}

const ReturnMenu: React.FC<ReturnMenuProps> = ({ patientId }) => {
  const { t } = useTranslate();
  const [selectedValue, setSelectedValue] = useState('pending');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [sendBackNoteDialogOpen, setSendBackNoteDialogOpen] = useState(false);
  const [pendingSendBackSrumIds, setPendingSendBackSrumIds] = useState<number[]>([]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'success',
  });

  const showSnackbar = (message: string, type: 'success' | 'error' = 'success', title?: string) => {
    setSnackbar({
      open: true,
      message,
      type,
      title: title || (type === 'success' ? t('success') : t('error')),
    });
  };



  const getStateIdFromValue = (value: string): number => {
    switch (value) {
      case 'pending':
        return 0; // Beklemede
      case 'sent':
        return 5; // Gönderildi (İade İsteği)
      case 'approved':
        return 4; // Onaylandı (İade)
      default:
        return 0;
    }
  };

  const stateId = getStateIdFromValue(selectedValue);

  const apiEndpoint = useMemo(() => {
    if (!patientId) return '';
    return `/nurse/api/Order/GetReturnedPatientOrders?patientId=${patientId}&stateId=${stateId}`;
  }, [patientId, stateId]);

  const { data: apiData, isLoading, error, refetch } = useBaseQuery<ReturnedPatientOrder[]>({
    endpoint: apiEndpoint,
    queryKey: ['returned-patient-orders', patientId, stateId],
    options: {
      enabled: !!apiEndpoint,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    setSelectedRows([]);
  };

  const handleSendSelected = async () => {
    if (selectedRows.length === 0) return;

    // Seçili satırlardan srumid'leri topla
    const srumIds = selectedRows
      .map((rowOrId) => {
        const row = typeof rowOrId === 'object' ? rowOrId : rows.find((r) => String(r.id) === String(rowOrId));
        // srumid, srumId veya srdid hangisi varsa onu kullan
        return row?.srumid;
      })
      .filter((id): id is number => id !== undefined && id !== null);

    if (srumIds.length === 0) {
      showSnackbar(t('nurse.order-medicine.return-menu.no-srumid-error'), 'error');
      return;
    }

    setPendingSendBackSrumIds(srumIds);
    setSendBackNoteDialogOpen(true);
  };

  const handleConfirmSendBack = async (note: string) => {
    if (pendingSendBackSrumIds.length === 0) {
      setSendBackNoteDialogOpen(false);
      return;
    }

    // Not dialogunu hemen kapat, sadece global loading/backdrop görünsün
    setSendBackNoteDialogOpen(false);

    try {
      setIsSending(true);
      const response = await nurseService.order.approveSendBackAsync<{ success?: boolean; message?: string; data?: any }>({
        srumids: pendingSendBackSrumIds,
        sendbacknote: note?.trim() ? note.trim() : null,
      });

      const isSuccess = (response as any)?.success === true || typeof (response as any)?.data === 'number';
      const serverMessage = (response as any)?.message;

      if (isSuccess) {
        // Backend mesajı yerine kendi sabit mesajımızı göster
        showSnackbar(t('nurse.order-medicine.return-menu.send-success') || 'Order başarıyla iadeye gönderildi.', 'success');
        setSelectedRows([]);
        setPendingSendBackSrumIds([]);
        refetch();
      } else {
        showSnackbar(serverMessage || t('nurse.order-medicine.return-menu.send-error'), 'error');
      }
    } catch (error) {
      console.error('Error sending for return approval:', error);
      showSnackbar(t('nurse.order-medicine.return-menu.send-error'), 'error');
    } finally {
      setIsSending(false);
    }
  };


  const rows = useMemo(() => {
    let list: ReturnedPatientOrder[] = [];

    if (Array.isArray(apiData)) {
      list = apiData as ReturnedPatientOrder[];
    } else if (apiData && Array.isArray((apiData as any).data)) {
      list = (apiData as any).data as ReturnedPatientOrder[];
    }

    if (list.length === 0) return [];

    // Üstte seçilen filtreye göre gridde gösterilecek status label'ı
    let statusLabel = '';
    switch (selectedValue) {
      case 'pending':
        statusLabel = 'Beklemede';
        break;
      case 'sent':
        statusLabel = 'Gönderildi';
        break;
      case 'approved':
        statusLabel = 'Onaylandı';
        break;
      default:
        statusLabel = '';
    }

    return list.map((item, index) => {

      return {
        id: `${item.srdid}-${index}`,
        orderTime: item.plandate ? dayjs(item.plandate).format('DD.MM.YYYY HH:mm') : '-',
        status: statusLabel || item.statusname || item.reqstatusname || '-',
        medicine: item.matname || item.srummatnames || '-',
        hyi: false,
        frequency: item.fqcanddose || '-',
        dose: item.appdosedet || '-',
        usageForm: '-',
        usagePurpose: '-',
        requestNote: '-',
        pharmacyNote: '-',
        lh: false,
        narcotic: false,
        isMixing: item.ismixinginfo === '1' || String(item.ismixinginfo) === '1',
        rawData: item,
        srdid: item.srdid,
        srumid: item.srumid ?? (item as any).srumId ?? (item as any).SrumId ?? (item as any).id ?? item.srdid,
      };
    });
  }, [apiData, selectedValue]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'orderTime',
        headerName: t('nurse.order-medicine.return-menu.columns.order-time'),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'status',
        headerName: t('nurse.order-medicine.return-menu.columns.status'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'medicine',
        headerName: t('nurse.order-medicine.return-menu.columns.medicine'),
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Box sx={{ fontWeight: 500, letterSpacing: '0.17px', lineHeight: '143%' }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'hyi',
        headerName: t('nurse.order-medicine.return-menu.columns.hyi'),
        flex: 0.5,
        minWidth: 80,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ByzMuiCheckBox checked={params.value} disabled />
          </Box>
        ),
      },
      {
        field: 'frequency',
        headerName: t('nurse.order-medicine.return-menu.columns.frequency-dose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'dose',
        headerName: t('nurse.order-medicine.return-menu.columns.drug-dose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'usageForm',
        headerName: t('nurse.order-medicine.return-menu.columns.usage-form'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'usagePurpose',
        headerName: t('nurse.order-medicine.return-menu.columns.usage-purpose'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'requestNote',
        headerName: t('nurse.order-medicine.return-menu.columns.request-note'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'pharmacyNote',
        headerName: t('nurse.order-medicine.return-menu.columns.pharmacy-note'),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'lh',
        headerName: t('nurse.order-medicine.return-menu.columns.lh'),
        flex: 0.5,
        minWidth: 60,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.value && (
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.38)',
                }}
              />
            )}
          </Box>
        ),
      },
      {
        field: 'narcotic',
        headerName: t('nurse.order-medicine.return-menu.columns.narcotic'),
        flex: 0.5,
        minWidth: 80,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.value && (
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.38)',
                }}
              />
            )}
          </Box>
        ),
      },
    ],
    [t]
  );

  const renderGridContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress size={48} sx={{ color: '#2979C3' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: 'error.main',
          }}
        >
          {t('nurse.order-medicine.return-menu.error')}
        </Box>
      );
    }

    return (
      <ByzMuiDataGrid
        rows={rows}
        columns={columns}
        pageSize={10}
        whiteGrid={true}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection as any[]);
        }}
        sx={{
          marginLeft: '8px',
          marginRight: '8px',
        }}
      />
    );
  };

  return (
    <>
      <Backdrop
        open={isSending}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>
          {t('loading')}
        </Typography>
      </Backdrop>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
      {/* Menu Bar */}
      <Box
        sx={{
          width: '100%',
          height: '59px',
          boxShadow: '0px 4px 4px rgba(120, 120, 120, 0.25)',
          borderRadius: '20px',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '0px 21px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '39px',
            width: '100%',
          }}
        >
          {/* Seçilileri Gönder Butonu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ByzMuiButton
              onClick={handleSendSelected}
              disabled={selectedRows.length === 0 || selectedValue !== 'pending' || isSending}
              sx={{
                height: '30px',
                width: '157px',
                borderRadius: '20px',
                backgroundColor: 'rgba(99, 180, 255, 0.2)',
                border: '1px solid #2979C3',
                color: '#2979C3',
                fontSize: '13px',
                lineHeight: '22px',
                textTransform: 'capitalize',
                padding: '4px 10px',
                '&:hover': {
                  backgroundColor: 'rgba(99, 180, 255, 0.3)',
                },
              }}
            >
              {isSending ? <CircularProgress size={20} sx={{ color: '#2979C3' }} /> : t('nurse.order-medicine.return-menu.send-selected')}
            </ByzMuiButton>

          </Box>

          {/* Radio Button Group */}
          <Box
            sx={{
              width: '554px',
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <RadioGroup
              row
              value={selectedValue}
              onChange={handleChange}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '40px',
              }}
            >
              <FormControlLabel
                value="pending"
                control={
                  <Radio
                    sx={{
                      padding: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '12px',
                      letterSpacing: '0.15px',
                      lineHeight: '150%',
                      color: 'rgba(0, 0, 0, 0.87)',
                      marginLeft: '8px',
                    }}
                  >
                    {t('nurse.order-medicine.return-menu.pending')}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  width: '81px',
                }}
              />
              <FormControlLabel
                value="sent"
                control={
                  <Radio
                    sx={{
                      padding: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '12px',
                      letterSpacing: '0.15px',
                      lineHeight: '150%',
                      color: 'rgba(0, 0, 0, 0.87)',
                      marginLeft: '8px',
                    }}
                  >
                    {t('nurse.order-medicine.return-menu.sent')}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  width: '87px',
                }}
              />
              <FormControlLabel
                value="approved"
                control={
                  <Radio
                    sx={{
                      padding: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '12px',
                      letterSpacing: '0.15px',
                      lineHeight: '150%',
                      color: 'rgba(0, 0, 0, 0.87)',
                      marginLeft: '8px',
                    }}
                  >
                    {t('nurse.order-medicine.return-menu.approved')}
                  </Typography>
                }
                sx={{
                  margin: 0,
                  width: 'auto',
                }}
              />
            </RadioGroup>
          </Box>
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ width: '100%' }}>
        <Card sx={{ marginBottom: '5px' }}>{renderGridContent()}</Card>
      </Box>

      <ByzMuiSnackBar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        title={snackbar.title}
        message={snackbar.message}
        type={snackbar.type}
      />

      <CancelDialog
        open={sendBackNoteDialogOpen}
        onClose={() => setSendBackNoteDialogOpen(false)}
        onConfirm={handleConfirmSendBack}
        title={t('nurse.order-medicine.return-menu.send-note.title')}
        description={t('nurse.order-medicine.return-menu.send-note.description')}
        placeholder={t('nurse.order-medicine.return-menu.send-note.placeholder')}
        cancelText={t('buttons.cancel')}
        confirmText={t('nurse.order-medicine.return-menu.send-note.confirm')}
        requireInput={false}
        width="650px"
        height="420px"
      />
      </Box>
    </>
  );
};

export default ReturnMenu;
